import { useCallback, useEffect, useRef, useState } from "react";
import {
  getHuelleColumnSettings,
  saveHuelleColumnSettings,
  type HuelleColumnSettings,
  type HuelleColumnView,
} from "@/lib/wartungen";
import { clampHuelleColumnWidth } from "./columns";

export type { HuelleColumnSettings, HuelleColumnView };

const WIDTHS_STORAGE_KEY = "eh:wartungen:huelle-detail-table-widths";

function readLegacyWidths(): Record<string, number> {
  try {
    const raw = localStorage.getItem(WIDTHS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, number>;
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) {
      const num = Number(value);
      if (Number.isFinite(num) && num > 0) out[key] = clampHuelleColumnWidth(num);
    }
    return out;
  } catch {
    return {};
  }
}

export function useHuelleTableSettings() {
  const [settings, setSettings] = useState<HuelleColumnSettings>(() => {
    const legacyWidths = readLegacyWidths();
    if (Object.keys(legacyWidths).length) {
      return {
        desktop: { columns: [], widths: legacyWidths },
        mobile: null,
      };
    }
    return { desktop: null, mobile: null };
  });
  const [loaded, setLoaded] = useState(false);
  const isMountedRef = useRef(true);
  const widthTimerRef = useRef<number | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    void getHuelleColumnSettings()
      .then((next) => {
        if (!isMountedRef.current) return;
        const legacyWidths = readLegacyWidths();
        const hasServerDesktop = Boolean(next.desktop?.columns?.length || next.desktop?.widths);
        if (!hasServerDesktop && Object.keys(legacyWidths).length) {
          setSettings({
            desktop: { columns: next.desktop?.columns ?? [], widths: legacyWidths },
            mobile: next.mobile,
          });
        } else {
          setSettings(next);
        }
      })
      .catch(() => {
        /* lokale Defaults behalten */
      })
      .finally(() => {
        if (isMountedRef.current) setLoaded(true);
      });
    return () => {
      isMountedRef.current = false;
      if (widthTimerRef.current) window.clearTimeout(widthTimerRef.current);
    };
  }, []);

  const saveSettings = useCallback(async (next: HuelleColumnSettings) => {
    setSettings(next);
    try {
      const saved = await saveHuelleColumnSettings(next);
      if (isMountedRef.current) setSettings(saved);
      return saved;
    } catch (err) {
      console.warn("[Wartungshülle] saveColumnSettings failed:", err);
      return next;
    }
  }, []);

  const onColumnWidthChange = useCallback((key: string, width: number) => {
    const clamped = clampHuelleColumnWidth(width);
    setSettings((prev) => {
      const desktop: HuelleColumnView = {
        columns: prev.desktop?.columns ?? [],
        widths: { ...(prev.desktop?.widths ?? {}), [key]: clamped },
      };
      const next: HuelleColumnSettings = { ...prev, desktop };
      if (widthTimerRef.current) window.clearTimeout(widthTimerRef.current);
      widthTimerRef.current = window.setTimeout(() => {
        void saveHuelleColumnSettings(next).catch(() => {
          try {
            localStorage.setItem(WIDTHS_STORAGE_KEY, JSON.stringify(desktop.widths));
          } catch {
            /* ignore */
          }
        });
        widthTimerRef.current = null;
      }, 500);
      return next;
    });
  }, []);

  return { settings, saveSettings, onColumnWidthChange, loaded };
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = () => setMatches(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);

  return matches;
}
